import { Box, BoxProps, CSSObject, Theme } from '@mui/material';
import React, { forwardRef } from 'react';
import { agGridVariables } from './agGridVariables';
import { skodaAgThemeParams } from './agGridSkodaTheme';
import { agGridCssIcons, agGridIconsDefinition } from './agGridIcons';

export const agGridStyles = (theme: Theme): CSSObject => {
  const agTheme = skodaAgThemeParams(theme);

  return {
    width: '100%',
    // support applying global style on all elements maxWidth: '100%'
    '*': {
      maxWidth: 'none',
    },
    // Apply CSS variables only for non-themed tables of the old versions of ag grid (old versions of ag-grid, <v33, themes does not have ag-theme-params-* class)
    '&:not(:has([class*="ag-theme-params-"])),&.ag-theme-material': {
      ...agGridVariables(theme),
    },

    '.ag-row-last': { borderBottom: 'none' },
    '.ag-column-last .ag-header-cell-resize': { display: 'none' },

    // Row lines full width
    '.ag-center-cols-container': {
      minWidth: '100%',
    },

    '.ag-center-cols-viewport': {
      // reduce to 1 row from default 150px
      minHeight: '56px',
    },

    // # Header styles
    '& .ag-header-cell .ag-header-cell-text': {
      fontWeight: `${agTheme.headerFontWeight}`,
    },

    // # Header Separators (only when column resizing is not activated and not for the last column)
    '& .ag-header-cell:not(.ag-column-last) > .ag-header-cell-resize.ag-hidden': {
      display: 'block !important',
      cursor: 'initial',
    },

    // # Horizontal Scrollbar
    '& .ag-body-horizontal-scroll': {
      alignItems: 'center',
      px: '22px',
      borderTop: '1px solid',
      borderTopColor: theme.palette.surface.secondary,
      maxHeight: 'none !important',
      height: 'auto !important',
    },
    '& .ag-body-horizontal-scroll .ag-body-horizontal-scroll-viewport': {
      mt: '16px',
      mb: '23px',
    },
    '& .ag-body-horizontal-scroll.ag-invisible .ag-body-horizontal-scroll-viewport': {
      my: 0,
    },

    // # Pagination styles
    '& .ag-paging-panel': {
      justifyContent: 'center',
      px: '24px',
    },

    // Pagination layout - rows per page centered, arrows right-aligned
    '& .ag-paging-panel .ag-paging-page-size': {
      position: 'absolute',
    },
    '& .ag-paging-panel .ag-paging-page-summary-panel': {
      marginLeft: 'auto',
    },

    // Disable pagination summary
    '& .ag-paging-row-summary-panel': {
      display: 'none',
    },

    // Text styling within pagination arrows
    '& .ag-paging-description': {
      fontSize: '18px',
      px: '18px',
    },
    '& .ag-paging-panel .ag-paging-description span': {
      color: 'text.primary',
      px: '2px',
    },
    '& .ag-paging-panel .ag-paging-description .ag-paging-number': {
      fontWeight: '700',
    },
    '& .ag-paging-panel .ag-paging-description span:last-child': {
      fontWeight: '300',
    },

    // Disable AG-grid pagination icons - start, end
    '& .ag-paging-panel .ag-icon-first, & .ag-paging-panel .ag-icon-last': {
      display: 'none',
    },

    // Pagination - rows per page
    '& .ag-paging-panel .ag-picker-field-wrapper': {
      border: 'none',
      px: '0',
      cursor: 'pointer',
      minWidth: 'auto',
    },
    '& .ag-paging-panel .ag-picker-field-display': {
      minWidth: '28px',
      width: 'auto',
      pr: '6px',
      fontSize: '18px',
      fontWeight: '700',
      textAlign: 'center',
    },
    '& .ag-paging-panel .ag-label': {
      mr: '6px',
      fontSize: '18px',
    },

    '& .ag-popup > *': {
      backgroundColor: 'onSurface.os0',
      cursor: 'pointer',
      borderRadius: '0px',
    },

    // # Opt-in className="skoda-ag-pointer"
    '& .skoda-ag-row-pointer .ag-row:hover': {
      cursor: 'pointer',
    },

    // Additional icons
    ...agGridCssIcons,

    // Show icons according to the SkodaFlow design
    '& .ag-icon-asc, & .ag-icon-desc': {
      color: theme.palette.onSurface.os600,
    },
    '& .ag-icon-asc:hover, & .ag-icon-desc:hover': {
      color: theme.palette.text.primary,
    },
    '& .ag-header-cell-sortable .ag-sort-indicator-container:has(.ag-sort-descending-icon.ag-hidden) .ag-hidden:has(.ag-icon-asc)':
      {
        display: 'inline-block !important', // Ag grid also uses !important so we have to override it
      },

    '& .ag-header-cell-sortable .ag-hidden .ag-icon-asc::before': {
      content: '""',
      display: 'flex',
      width: '17px',
      height: '17px',
      maskSize: 'contain',
      maskRepeat: 'no-repeat',
      maskPosition: 'center',
      verticalAlign: 'middle',
      maskImage: `url(data:image/svg+xml;utf8,${encodeURIComponent(agGridIconsDefinition.ascHover.svg)})`,
    },
    '& .ag-header-cell-sortable .ag-hidden .ag-icon-asc:hover::before': {
      maskImage: `url(data:image/svg+xml;utf8,${encodeURIComponent(agGridIconsDefinition.asc.svg)})`,
    },

    // Legacy class approach, no theme
    fontFamily: agTheme.fontFamily,
  };
};

interface SkodaAgGridProviderProps extends BoxProps {}

/**
 * A provider wrapper that applies Skoda AG Grid theming via sx.
 */
export const SkodaAgGridProvider: React.FC<SkodaAgGridProviderProps> = forwardRef(({ sx, children, ...rest }, ref) => {
  return (
    <Box
      sx={(theme) => ({
        ...agGridStyles(theme),
        ...sx,
      })}
      ref={ref}
      {...rest}
    >
      {children}
    </Box>
  );
});
